import { useState, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Upload, Download, CheckCircle2, AlertCircle, Info } from 'lucide-react';
import { saveOrShareBlob } from '@/lib/nativeFile';
import { CrmImportResultado } from '@/types/crm';

interface CrmImportCsvDialogProps<R extends CrmImportResultado> {
  titulo: string;
  templateNomeArquivo: string;
  templateColunas: string[];
  templateExemplo: string[];
  onImport: (file: File) => Promise<{ success: boolean; data?: R | null; error?: string }>;
  onSuccess: () => void;
  disabled?: boolean;
  disabledMotivo?: string;
  /** Tipos aceitos no seletor de arquivo (padrão: CSV). */
  accept?: string;
  /** Texto do botão do modelo (padrão: "Baixar modelo CSV"). */
  rotuloModelo?: string;
  /** Gera o modelo (ex.: um .xlsx); sem isso, monta um CSV com `templateColunas` + `templateExemplo`. */
  onBaixarModelo?: () => Promise<void>;
  /** Orientação curta abaixo do seletor de arquivo. */
  ajuda?: ReactNode;
  /** Linha extra no resultado (ex.: "11 criados · 3 atualizados"). */
  detalheResultado?: (resultado: R) => ReactNode;
}

// Componente genérico reaproveitado por Negócios/Contas/Contatos e pela importação de Cargos (Excel)
// — muda o template, os tipos aceitos e o callback de import. Fluxo simples de 2 estados dentro do
// mesmo Dialog (o projeto não tem padrão de wizard, ver pesquisa da rodada de P2/Import CSV):
// 1) selecionar arquivo, 2) resultado (sucesso/erros por linha).
export function CrmImportCsvDialog<R extends CrmImportResultado = CrmImportResultado>({
  titulo,
  templateNomeArquivo,
  templateColunas,
  templateExemplo,
  onImport,
  onSuccess,
  disabled,
  disabledMotivo,
  accept = '.csv,text/csv',
  rotuloModelo = 'Baixar modelo CSV',
  onBaixarModelo,
  ajuda,
  detalheResultado,
}: CrmImportCsvDialogProps<R>) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState<R | null>(null);
  const [erroGeral, setErroGeral] = useState<string | null>(null);

  const resetState = () => {
    setFile(null);
    setResultado(null);
    setErroGeral(null);
    setLoading(false);
  };

  const handleOpenChange = (novoOpen: boolean) => {
    setOpen(novoOpen);
    if (!novoOpen) {
      resetState();
      if (resultado && resultado.importados > 0) onSuccess();
    }
  };

  const handleBaixarModelo = async () => {
    if (onBaixarModelo) {
      await onBaixarModelo();
      return;
    }
    const csv = [templateColunas.join(','), templateExemplo.join(',')].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    await saveOrShareBlob(blob, templateNomeArquivo);
  };

  const handleImportar = async () => {
    if (!file) return;
    setLoading(true);
    setErroGeral(null);
    try {
      const result = await onImport(file);
      if (!result.success || !result.data) {
        setErroGeral(result.error || 'Erro ao importar arquivo');
        return;
      }
      setResultado(result.data);
    } catch (error) {
      setErroGeral(error instanceof Error ? error.message : 'Erro ao importar arquivo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        disabled={disabled}
        title={disabled ? disabledMotivo : undefined}
      >
        <Upload className="mr-2 h-4 w-4" />
        Importar
      </Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{titulo}</DialogTitle>
        </DialogHeader>

        {!resultado ? (
          <div className="space-y-4">
            <Button variant="outline" className="w-full" onClick={handleBaixarModelo}>
              <Download className="mr-2 h-4 w-4" />
              {rotuloModelo}
            </Button>
            <div>
              <input
                type="file"
                accept={accept}
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="w-full text-sm text-muted-foreground file:mr-3 file:rounded-md file:border file:bg-background file:px-3 file:py-1.5 file:text-sm file:font-medium"
              />
              {ajuda && <p className="mt-2 text-xs text-muted-foreground">{ajuda}</p>}
            </div>
            {erroGeral && (
              <p className="flex items-start gap-2 text-sm text-destructive">
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                {erroGeral}
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {resultado.importados > 0 ? (
              <p className="flex items-center gap-2 text-sm font-medium">
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                {resultado.importados} de {resultado.total} importados com sucesso
              </p>
            ) : (
              // Nada gravado (tudo com erro ou sem alteração): sem o check verde de sucesso.
              <p className="flex items-center gap-2 text-sm font-medium">
                <Info className="h-4 w-4 text-muted-foreground" />
                Nenhuma linha importada ({resultado.total} lida{resultado.total === 1 ? '' : 's'})
              </p>
            )}
            {detalheResultado && <p className="text-sm text-muted-foreground">{detalheResultado(resultado)}</p>}
            {resultado.erros.length > 0 && (
              <div className="max-h-48 space-y-1 overflow-y-auto rounded-md border p-2 text-sm">
                {resultado.erros.map((erro) => (
                  <p key={erro.linha} className="flex items-start gap-1.5 text-destructive">
                    <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                    Linha {erro.linha}: {erro.motivo}
                  </p>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {!resultado ? (
            <>
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                Cancelar
              </Button>
              <Button onClick={handleImportar} disabled={!file || loading}>
                {loading ? 'Importando...' : 'Importar'}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={resetState}>
                Importar outro arquivo
              </Button>
              <Button onClick={() => handleOpenChange(false)}>Fechar</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
